import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./Tooltip.scss";
import { motion, useIsPresent } from "framer-motion";

const updateTooltipPos = (
	{ x, y },
	initialMousePos,
	tooltipEl,
	lockQuartile,
	storedPosRight,
	storedPosBottom,
	storedMinMaxXY
) => {
	const { innerWidth: w, innerHeight: h } = window;

	const { x: initialX, y: initialY } = initialMousePos ?? { x, y };
	// check which quartile of the screen mouse is in
	const posRight = lockQuartile.current
		? storedPosRight.current
		: initialX > w * 0.5;
	const posBottom = lockQuartile.current
		? storedPosBottom.current
		: initialY > h * 0.5;

	const tooltipDimensions = tooltipEl.current?.getBoundingClientRect();

	const minX = posRight ? 0 : tooltipDimensions.width;
	const minY = posBottom ? 0 : tooltipDimensions.height;
	const maxX = posRight ? w - tooltipDimensions.width : w;
	const maxY = posBottom ? h - tooltipDimensions.height : h;

	const tooltipX = Math.min(
		lockQuartile.current ? storedMinMaxXY.current.maxX : maxX,
		Math.max(lockQuartile.current ? storedMinMaxXY.current.minX : minX, x)
	);
	const tooltipY = Math.min(
		lockQuartile.current ? storedMinMaxXY.current.maxY : maxY,
		Math.max(lockQuartile.current ? storedMinMaxXY.current.minY : minY, y)
	);

	if (tooltipEl.current && tooltipEl.current.children[0]) {
		tooltipEl.current.style.transform = `translate(${tooltipX}px, ${tooltipY}px)`;
		if (!lockQuartile.current)
			tooltipEl.current.children[0].style.transform = `translate(${
				posRight ? 0 : "-100%"
			}, ${posBottom ? 0 : "-100%"})`;
	}
};

export function Tooltip({
	title,
	subtitle,
	summary,
	score,
	category,
	modalOpen,
	initialMousePos,
	draggingMousePos,
	mousePosition,
}) {
	const tooltipEl = useRef();
	const lockQuartile = useRef(false);
	const storedPosRight = useRef(true);
	const storedPosBottom = useRef(true);
	const storedMinMaxXY = useRef({});
	const isPresent = useIsPresent();
	const [isDragging, setIsDragging] = useState(false);
	const prevIsDragging = useRef(isDragging);

	useEffect(
		() => draggingMousePos.onChange(({ x, y }) => setIsDragging(!!x && !!y)),
		[draggingMousePos]
	);

	// debugger;

	useLayoutEffect(() => {
		let lockTimeout;
		let cleanup = draggingMousePos.onChange(({ x, y }) => {
			if (x || y) {
				updateTooltipPos(
					{ x, y },
					initialMousePos,
					tooltipEl,
					lockQuartile,
					storedPosRight,
					storedPosBottom,
					storedMinMaxXY
				);
			}

			// lock position when dragging as sometimes dragging causes hoverItem to change but then go back to initial hoverItem quickly and we don't want the quartile position to change in this case
			if (!lockQuartile.current) {
				const { innerWidth: w, innerHeight: h } = window;
				const { x: initialX, y: initialY } =
					initialMousePos ?? draggingMousePos;

				// check which quartile of the screen mouse is in
				const posRight = initialX > w * 0.5;
				const posBottom = initialY > h * 0.5;

				const tooltipDimensions = tooltipEl.current?.getBoundingClientRect();

				const minX = posRight ? 0 : tooltipDimensions.width;
				const minY = posBottom ? 0 : tooltipDimensions.height;
				const maxX = posRight ? w - tooltipDimensions.width : w;
				const maxY = posBottom ? h - tooltipDimensions.height : h;

				storedPosRight.current = posRight;
				storedPosBottom.current = posBottom;
				storedMinMaxXY.current = { minX, minY, maxX, maxY };
			}
			clearTimeout(lockTimeout);
			if (x || y) {
				lockQuartile.current = true;

				lockTimeout = setTimeout(() => {
					lockQuartile.current = false;
				}, 750);
			}
		});

		return () => {
			cleanup();
			clearTimeout(lockTimeout);
		};
	}, [
		draggingMousePos,
		initialMousePos,
		tooltipEl,
		lockQuartile,
		storedPosRight,
		storedPosBottom,
		storedMinMaxXY,
	]);

	useLayoutEffect(() => {
		if (isPresent && !isDragging && isDragging === prevIsDragging.current) {
			let { x, y } = mousePosition.get();
			updateTooltipPos(
				{ x, y },
				initialMousePos,
				tooltipEl,
				lockQuartile,
				storedPosRight,
				storedPosBottom,
				storedMinMaxXY
			);
		}
		return mousePosition.onChange(({ x, y }) => {
			if (isPresent && !isDragging) {
				updateTooltipPos(
					{ x, y },
					initialMousePos,
					tooltipEl,
					lockQuartile,
					storedPosRight,
					storedPosBottom,
					storedMinMaxXY
				);
			}
		});
	}, [
		mousePosition,
		initialMousePos,
		tooltipEl,
		lockQuartile,
		storedPosRight,
		storedPosBottom,
		storedMinMaxXY,
		isPresent,
		isDragging,
	]);
	useEffect(() => {
		prevIsDragging.current = isDragging;
	}, [isDragging]);
	return (
		<motion.div
			className={`tooltip ${modalOpen ? "tooltip--modal-open" : ""}`}
			ref={tooltipEl}>
			<div className="tooltip__inner">
				<motion.div
					initial={{ transform: "translateY(10px)", opacity: 0 }}
					animate={{ transform: "translateY(0px)", opacity: 1 }}
					exit={{ transform: "translateY(-10px)", opacity: 0 }}
					transition={{ type: "tween", duration: 0.3 }}
					className="tooltip__panel">
					<div className="tooltip__title">
						<div
							className={`tooltip__title__dot tooltip__title__dot--${category?.toLowerCase()}`}
						/>
						<h2>{title}</h2>
					</div>
					<div
						className="tooltip__description"
						dangerouslySetInnerHTML={{ __html: summary }}
					/>
					<p className="tooltip__score">
						<span className="tooltip__score__title">Total score:</span>
						<span className="tooltip__score__value">
							{score ? `${score}/10` : "N/A"}
						</span>
					</p>
				</motion.div>
			</div>
		</motion.div>
	);
}
